import React from 'react';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import CustomSplitColumnCard from '@common/components/misc/CustomSplitColumnCard';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../../store';
import { HardwareIcon, InfoIcon, SoftwareIcon } from '@common/utils/icons/icons';
import { selectRobotsData } from '../../../store/robots/robots-slice-selectors';
import CustomLabelTextTable from '../../misc/CustomLabelTextTable';

const SystemInformation: React.FC = () => {
  const { t } = useTranslation();

  const robotsData = useAppSelector((state) => selectRobotsData(state));

  const robot = robotsData.currentRobot;

  const get_hardware_version_localization = (type: string, value: string | null | undefined) => {
    if (value == null || value.length === 0) {
      return t('HardwareVersioningOption-not-configured');
    }
    const key = `HardwareVersioningOption.${type}.${value}`;
    if (t(key) !== key) {
      return t(key);
    }
    return value;
  };

  return (
    <React.Fragment>
      <CustomSplitColumnCard
        title={t('title-system-settings')}
        content={[
          {
            left: (
              <Stack direction="row" alignItems="center" spacing={1} py={1}>
                <InfoIcon />
                <Typography>{t('title-robot-general-settings')}</Typography>
              </Stack>
            ),
            right: (
              <CustomLabelTextTable
                cellPadding={1}
                rows={[
                  { label: t('table-header-serial-number'), text: robot?.serialNumber },
                  {
                    label: t('table-header-robot-version'),
                    text: robot ? t('robot-model-' + robot?.model) : '',
                  },
                  {
                    label: t('table-header-robot-generation'),
                    text: robot
                      ? t('HardwareVersioningOption.robot_generation.' + robot?.robotGeneration)
                      : '',
                  },
                  {
                    label: t('table-header-os-variant'),
                    text: robot ? t('os-variant-' + robot?.osVariant) : '',
                  },
                ]}
              />
            ),
          },
          {
            left: (
              <Stack direction="row" alignItems="center" spacing={1} py={1}>
                <SoftwareIcon />
                <Typography>{t('title-robot-software-settings')}</Typography>
              </Stack>
            ),
            right: (
              <CustomLabelTextTable
                cellPadding={1}
                rows={[
                  {
                    label: t('table-header-build-version'),
                    text: robot?.softwareVersion?.buildVersion,
                  },
                  {
                    label: t('table-header-rsw-software-version'),
                    text: robot?.softwareVersion?.rswSoftwareVersion,
                  },
                  {
                    label: t('table-header-web-software-version'),
                    text: robot?.softwareVersion?.webSoftwareVersion,
                  },
                  {
                    label: t('table-header-mgmt-software-version'),
                    text: robot?.softwareVersion?.mgmtSoftwareVersion,
                  },
                  {
                    label: t('table-header-proxy-software-version'),
                    text: robot?.softwareVersion?.proxySoftwareVersion,
                  },
                ]}
              />
            ),
          },
          robot?.hardwareVersion != null
            ? {
                left: (
                  <Stack direction="row" alignItems="center" spacing={1} py={1}>
                    <HardwareIcon />
                    <Typography>{t('title-robot-hardware-settings')}</Typography>
                  </Stack>
                ),
                right: (
                  <CustomLabelTextTable
                    cellPadding={1}
                    rows={[
                      {
                        label: t('table-header-firmware-version'),
                        text: robot?.hardwareVersion?.firmwareVersion,
                      },
                      {
                        label: t('table-header-baseboard-version'),
                        text: robot?.hardwareVersion?.baseboardVersion,
                      },
                      {
                        label: t('table-header-powerboard-version'),
                        text: robot?.hardwareVersion?.powerboardVersion,
                      },
                      {
                        label: t('table-header-camera-version'),
                        text: get_hardware_version_localization(
                          'camera',
                          robot?.hardwareVersion?.cameraVersion,
                        ),
                      },
                      {
                        label: t('table-header-lidar-version'),
                        text: get_hardware_version_localization(
                          'lidar',
                          robot?.hardwareVersion?.lidarVersion,
                        ),
                      },
                      {
                        label: t('table-header-som-version'),
                        text: get_hardware_version_localization(
                          'som',
                          robot?.hardwareVersion?.somVersion,
                        ),
                      },
                      {
                        label: t('table-header-battery-serial-number'),
                        text: robot?.hardwareVersion?.batterySerialNumber,
                      },
                      {
                        label: t('table-header-wpa-enabled'),
                        text:
                          robot?.hardwareVersion?.wpaEnabled == null
                            ? t('unknown-value')
                            : robot?.hardwareVersion.wpaEnabled
                              ? t('wpa_enabled_true')
                              : t('wpa_enabled_false'),
                      }, // true == wifi false, gsm
                      {
                        label: t('table-header-gsm-dongle'),
                        text: get_hardware_version_localization(
                          'gsm_dongle',
                          robot?.hardwareVersion?.gsmDongle,
                        ),
                      },
                      {
                        label: t('table-header-wlan-antenna-1'),
                        text: get_hardware_version_localization(
                          'wlan_dongle_1',
                          robot?.hardwareVersion?.wlanAntenna1,
                        ),
                      },
                      {
                        label: t('table-header-wlan-antenna-2'),
                        text: get_hardware_version_localization(
                          'wlan_dongle_2',
                          robot?.hardwareVersion?.wlanAntenna2,
                        ),
                      },
                      {
                        label: t('table-header-filter-cleaner'),
                        text: robot?.hardwareVersion?.filterCleaner,
                      },
                      {
                        label: t('table-header-flasher'),
                        text: get_hardware_version_localization(
                          'flasher',
                          robot?.hardwareVersion?.flasher,
                        ),
                      },
                      {
                        label: t('table-header-fan'),
                        text: get_hardware_version_localization(
                          'blower',
                          robot?.hardwareVersion?.fan,
                        ),
                      },
                      {
                        label: t('table-header-cover'),
                        text: get_hardware_version_localization(
                          'cover',
                          robot?.hardwareVersion?.cover,
                        ),
                      },
                      {
                        label: t('table-header-front-brushes'),
                        text: get_hardware_version_localization(
                          'front_brushes',
                          robot?.hardwareVersion?.frontBrushes,
                        ),
                      },
                      {
                        label: t('table-header-roller-brush'),
                        text: get_hardware_version_localization(
                          'roller_brush',
                          robot?.hardwareVersion?.rollerBrush,
                        ),
                      },
                      {
                        label: t('table-header-wheels'),
                        text: get_hardware_version_localization(
                          'wheel',
                          robot?.hardwareVersion?.wheels,
                        ),
                      },
                    ]}
                  />
                ),
              }
            : null,
        ]}
      />
    </React.Fragment>
  );
};

export default SystemInformation;
